import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { getSupabaseBrowser, type Session, type User } from "@universe/supabase/browser";
import { featureFlags } from "../utilities/featureFlags";

// Auth events that trigger specific UI states
export type AuthEvent = "PASSWORD_RECOVERY" | null;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authModalShowing: boolean;
  authEvent: AuthEvent;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  clearAuthEvent: () => void;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(featureFlags.shop);
  const [authModalShowing, setAuthModalShowing] = useState(false);
  const [authEvent, setAuthEvent] = useState<AuthEvent>(null);

  const clearAuthEvent = useCallback(() => {
    setAuthEvent(null);
  }, []);

  const openAuthModal = useCallback(() => {
    if (featureFlags.shop) setAuthModalShowing(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalShowing(false);
    clearAuthEvent();
  }, [clearAuthEvent]);

  const getAccessToken = useCallback(() => {
    return session?.access_token ?? null;
  }, [session]);

  // Initialize auth state and listen for changes
  useEffect(() => {
    if (!featureFlags.shop) return;
    const supabase = getSupabaseBrowser();
    let cancelled = false;

    // Listen for auth state changes - Supabase automatically detects URL hash tokens
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (cancelled) return;

        setSession(newSession ?? null);
        setIsLoading(false);

        // Handle specific auth events that need UI response
        if (event === "PASSWORD_RECOVERY") {
          setAuthEvent("PASSWORD_RECOVERY");
          setAuthModalShowing(true);
        }
      }
    );

    // Check for token_hash in URL (PKCE flow) - requires explicit verifyOtp call
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("token_hash");
    const type = params.get("type");

    if (tokenHash && type) {
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: type as any }).then(({ error }) => {
        if (!cancelled) {
          window.history.replaceState({}, "", window.location.pathname);
          if (error) {
            console.error("Error verifying OTP:", error.message);
          }
        }
      });
    } else {
      // For implicit flow (hash fragments), Supabase handles it automatically
      // Just need to call getSession to trigger the detection
      supabase.auth.getSession().then(({ data }) => {
        if (!cancelled) {
          setSession(data.session ?? null);
          setIsLoading(false);
        }
      });
    }

    return () => {
      cancelled = true;
      subscription?.subscription?.unsubscribe();
    };
  }, []);

  // Auto-open auth modal if user needs to verify email
  useEffect(() => {
    if (session?.user && !session.user.email_confirmed_at) {
      setAuthModalShowing(true);
    }
  }, [session?.user?.id, session?.user?.email_confirmed_at]);

  const value: AuthContextType = {
    session,
    user: session?.user ?? null,
    isLoading,
    isAuthenticated: Boolean(session?.user),
    authModalShowing,
    authEvent,
    openAuthModal,
    closeAuthModal,
    clearAuthEvent,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
